/**
 * Auth API для работы с FastAPI backend v2.0
 * 
 * Реализует методы аутентификации:
 * - register - регистрация
 * - login - вход
 * - logout - выход
 * - me - получение текущего пользователя
 * - refresh - обновление токена
 */

import { post, get } from "@/lib/api-client-v2";
import { setToken, removeToken } from "@/lib/api-client-v2";
import type {
  LoginRequest,
  RegisterRequest,
  TokenResponse,
  User,
  UserPermissions,
} from "@/types/api-v2";

export const authApi = {
  /**
   * Регистрация нового пользователя
   */
  register: async (data: RegisterRequest): Promise<TokenResponse> => {
    const response = await post<TokenResponse>("/auth/register", data);
    // Автоматически сохраняем токен
    setToken(response.access_token);
    return response;
  },

  /**
   * Вход пользователя. Может вернуть MFA challenge без access_token.
   */
  login: async (data: LoginRequest): Promise<import("@/services/security.service").LoginResponse> => {
    const response = await post<import("@/services/security.service").LoginResponse>(
      "/auth/login",
      data
    );
    if (response.access_token && !response.mfa_required) {
      setToken(response.access_token);
    }
    return response;
  },

  mfaVerify: async (mfa_token: string, code: string): Promise<TokenResponse> => {
    const response = await post<TokenResponse>("/auth/mfa/verify", { mfa_token, code });
    setToken(response.access_token);
    return response;
  },

  /**
   * Выход пользователя
   */
  logout: async (): Promise<void> => {
    try {
      await post("/auth/logout");
    } finally {
      // Всегда удаляем токен, даже если запрос failed
      removeToken();
    }
  },

  /**
   * Получение информации о текущем пользователе
   */
  me: async (): Promise<User> => {
    return get<User>("/auth/me");
  },

  /**
   * Обновление JWT токена
   */
  refresh: async (): Promise<TokenResponse> => {
    const response = await post<TokenResponse>("/auth/refresh");
    // Автоматически обновляем токен
    setToken(response.access_token);
    return response;
  },

  /**
   * Получение разрешений текущего пользователя
   */
  permissions: async (): Promise<UserPermissions> => {
    return get<UserPermissions>("/auth/permissions");
  },
};

